import { useCallback, useEffect, useRef } from 'react'
import QuestionsBuilderSelector from '@v4/utils/selectors/QuestionsBuilderSelector'
import QuestionsBuilderModel from '@v4/store/builderStore/models/QuestionsBuilder.model'
import AppStore from '@v4/store/appStore'
import PageMetaData from '@v4/utils/data/PageMetaData'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import PageBuilderModel from '@v4/store/builderStore/models/PageBuilder.model'
import GRID_SIZE from './OptionItem.const'
import useStyles from './OptionItem.styled'

export const useOptionItem = props => {
	const {
		id,
		showLabels,
		editable,
		multiple,
		deletable,
		loading,
		pending,
		checked,
		label,
		asset,
		draggable,
		questionId,
		answersPerRow,
		onDelete,
		onLabelEdit,
		onClick,
		autoFocus,
		placeholder,
	} = props
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const isQuestionBuilderMode = PageBuilderSelector.useIsQuestionsMode()
	const gallery = QuestionsBuilderSelector.useGallery()
	const entityType = 'question_option'
	const { setSidebarMode } = PageBuilderSelector.useActions()
	const { setScrollingMenu, setActiveOptionId, setQuestionTab, setGallery } = QuestionsBuilderSelector.useActions()
	const labelRef = useRef('')
	labelRef.current = label

	const classes = useStyles(props)
	const imagePath = _.isObject(asset) ? PageMetaData.imageThumbUrlFrom(asset.path, 640) : asset

	const buttonRef = useRef(null)

	const handleOpenGallery = useCallback(
		event => {
			event?.preventDefault()
			event?.stopPropagation()
			if (!isQuestionBuilderMode) {
				return
			}
			setTimeout(() => {
				setGallery({
					entityId: id,
					parentId: questionId,
					entityType,
					field: 'asset',
					active: true,
				})
			}, 0)
		},
		[id, isQuestionBuilderMode, questionId, setGallery],
	)

	const handleDelete = useCallback(() => {
		onDelete(id)
	}, [id, onDelete])

	const handleLabelEdit = useCallback(
		option => {
			onLabelEdit({ id, option })
		},
		[id, onLabelEdit],
	)

	const handleFocus = useCallback(() => {
		if (!isQuestionBuilderMode) {
			return
		}

		setGallery(null)

		setSidebarMode(PageBuilderModel.SIDEBAR_MODE.QUESTIONS)
		setScrollingMenu?.(true)
		setActiveOptionId(id)
		setQuestionTab(QuestionsBuilderModel.QUESTION_TAB.LOGIC)
	}, [id, isQuestionBuilderMode, setActiveOptionId, setGallery, setQuestionTab, setScrollingMenu, setSidebarMode])

	const handleLabelFocus = useCallback(() => {
		const pattern = /^Answer \d+$/g

		if (pattern.test(labelRef.current)) {
			handleLabelEdit('')
		}

		handleFocus()
	}, [handleFocus, handleLabelEdit])

	const handleClick = useCallback(
		event => {
			if (!isBuilderMode) {
				onClick(id)
				return
			}

			handleFocus()

			if (!imagePath) {
				handleOpenGallery(event)
			}
		},
		[isBuilderMode, handleFocus, imagePath, onClick, id, handleOpenGallery],
	)

	const handleClickGalleryIcon = useCallback(
		event => {
			setGallery(null)
			buttonRef.current?.focus()
			handleOpenGallery(event)
		},
		[handleOpenGallery, setGallery],
	)

	useEffect(() => {
		if (isQuestionBuilderMode && autoFocus) {
			handleClickGalleryIcon()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleKeyUp = useCallback(
		({ key }) => {
			if (key === 'Enter') {
				onClick(id)
			}
		},
		[id, onClick],
	)

	const handleKeyDown = useCallback(event => {
		if (event.key === 'Enter') {
			event.preventDefault()
		}
	}, [])

	const preview = gallery?.entityId === id && gallery?.entityType === entityType ? gallery.preview?.path : null

	return [
		{
			label,
			imagePath: preview || imagePath,
			classes,
			editable,
			deletable,
			checked,
			loading,
			disabled: pending,
			withLabels: showLabels,
			showCheckBox: multiple && checked,
			draggable,
			showGalleryIcon: isBuilderMode && editable,
			gridDesktopSize: GRID_SIZE[answersPerRow] || GRID_SIZE.DEFAULT,
			autoFocus,
			placeholder,
			buttonRef,
		},
		{
			handleDelete,
			handleClickGalleryIcon: isBuilderMode ? handleClickGalleryIcon : undefined,
			handleLabelEdit,
			handleLabelFocus: isBuilderMode && isQuestionBuilderMode ? handleLabelFocus : undefined,
			handleClick,
			handleKeyUp: !multiple && !isBuilderMode ? handleKeyUp : undefined,
			handleKeyDown: multiple && !isBuilderMode ? handleKeyDown : undefined,
		},
	]
}
